import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Icon } from '@/lib/icons';
import { AfricanKeyboard } from './AfricanKeyboard';
import { useApp } from '@/hooks/use-app-context';

interface KeyboardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  language: string;
  value: string;
  onChange: (value: string) => void;
}

export function KeyboardModal({
  open,
  onOpenChange,
  language,
  value,
  onChange,
}: KeyboardModalProps) {
  const { t } = useApp();
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    if (open) setInputValue(value);
  }, [open, value]);

  const handleConfirm = () => {
    onChange(inputValue);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-border/40 bg-background dark:bg-background/80 backdrop-blur-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{t.keyboardModalTitle.replace("{language}", language)}</span>
            <span className="text-xs font-normal text-muted-foreground">
              {t.keyboardModalHint.replace("{language}", language)}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-[0.26em] text-muted-foreground">
              {t.keyboardModalInputLabel}
            </label>
            <div className="min-h-[80px] rounded-xl border border-border/40 bg-background/60 p-4 text-sm leading-relaxed break-words backdrop-blur-xl">
              {inputValue || (
                <span className="italic text-muted-foreground/50">
                  {t.keyboardModalPlaceholder}
                </span>
              )}
            </div>
          </div>

          <AfricanKeyboard
            language={language}
            value={inputValue}
            onChange={setInputValue}
            onClose={() => onOpenChange(false)}
          />
        </div>

        <DialogFooter className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => setInputValue('')}
            className="mr-auto"
          >
            <Icon name="trash2" className="mr-2 h-4 w-4" />
            {t.keyboardModalClear}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t.keyboardModalCancel}
          </Button>
          <Button onClick={handleConfirm}>{t.keyboardModalConfirm}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
